/* import __COLOCATED_TEMPLATE__ from './suggestion-explore-panel.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import { restartableTask } from 'ember-concurrency-decorators';
import { inject as service } from '@ember/service';
import { type TaskGenerator } from 'ember-concurrency';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import type { ConversationExploreConversation } from 'embercom/components/reporting/automation/ai-insights/conversation-panel';
import { action } from '@ember/object';
import type InboxApiService from 'embercom/services/inbox-api';
import type Conversation from 'embercom/objects/inbox/conversation';
import type ContentReviewRequest from 'embercom/models/content-service/content-review-request';
import type { RelatedContentEntity } from 'embercom/services/suggestion-service';

interface Args {
  showSidePanel: boolean;
  onClose: () => void;
  activeTab: Tab;
  isStandalone: boolean;
  conversationCount: number;
  relatedContentCount: number;
  trackAnalyticsEvent: (action: string, object: string, extraContext: Record<string, any>) => void;
  reviewRequest: ContentReviewRequest;
  loadedRelatedEntities: RelatedContentEntity[];
  isLoadingRelatedContent: boolean;
}

const CONVERSATION_PAGE_SIZE = 100;

export type Tab = 'conversations' | 'related_content';

export default class SuggestionExplorePanel extends Component<Args> {
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @service declare appService: AppService;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare intl: IntlService;
  @service declare reportingChartService: ReportingChartService;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare inboxApi: InboxApiService;

  @tracked totalHits = 0;
  @tracked pageFrom = 0;
  @tracked loadedConversations: ConversationExploreConversation[] = [];
  @tracked _selectedTab: Tab | null = null;
  @tracked selectedConversationId = 0;

  fetchDataTask = taskFor(this.loadConversations);

  conversationData = trackedTask(this, this.fetchDataTask, () => [this.pageFrom]);

  get selectedTab() {
    return this._selectedTab || this.args.activeTab;
  }

  set selectedTab(tab: Tab) {
    this._selectedTab = tab;
    this.args.trackAnalyticsEvent('tab_changed', 'suggestion_explorer', {
      tab,
    });
  }

  get conversationFetchError() {
    return this.fetchDataTask.lastErrored?.error as any;
  }

  get isUnderperformingContent() {
    return this.args.reviewRequest?.isUnderperformingContent;
  }

  @restartableTask
  *loadConversations(): TaskGenerator<void> {
    let suggestion = this.args.reviewRequest?.suggestion;

    if (!suggestion) {
      return;
    }

    // Use unresolvedConversationIds for underperforming content, includedConversationIds for others
    let conversationIds = this.args.reviewRequest?.isUnderperformingContent
      ? suggestion.unresolvedConversationIds
      : suggestion.includedConversationIds;

    let uniqueConversationIds = [...new Set(conversationIds)];

    if (uniqueConversationIds?.length) {
      if (this.pageFrom === 0) {
        this.loadedConversations = [];
      }
      let conversations: Conversation[] = [];
      let conversationIdsToFetch = uniqueConversationIds.slice(
        this.pageFrom,
        this.pageFrom + CONVERSATION_PAGE_SIZE,
      );
      let conversationPromises = conversationIdsToFetch.map((conversationId) =>
        this.inboxApi.fetchConversation(conversationId),
      );

      conversations = yield Promise.all(conversationPromises);

      let newConversations = conversations.map((conversation) => ({
        id: Number(conversation.id),
        userName: conversation.firstParticipant?.name || '',
        userPseudonym: conversation.firstParticipant?.pseudonym || '',
        topicSummary:
          conversation.attributesById?.['ai_insights_conversation_summary']?.value || '',
        subtopic: conversation.attributesById?.['ai_insights_subtopic']?.value || '',
        topic: conversation.attributesById?.['ai_insights_topic']?.value || '',
        cxExplanation: this.appService.app.useNewCxSummary
          ? conversation.attributesById?.['customer_experience_summary']?.value || ''
          : conversation.attributesById?.['customer_experience_explanation']?.value || '',
        cxScore: conversation.attributesById?.['customer_experience_rating']?.value || '',
        cxAttributes: this.appService.app.useNewCxSummary
          ? conversation.attributesById?.['customer_experience_attributes']?.value || ''
          : undefined,
      })) as ConversationExploreConversation[];

      this.loadedConversations = [...this.loadedConversations, ...newConversations];

      this.totalHits = uniqueConversationIds.length || 0;
      this.selectedConversationId =
        this.loadedConversations.length > 0 ? Number(this.loadedConversations[0].id) : 0;
    }
  }

  @action
  loadMoreConversations() {
    this.pageFrom += CONVERSATION_PAGE_SIZE;
    this.args.trackAnalyticsEvent('load_more_conversations', 'suggestion_explorer', {
      pageFrom: this.pageFrom,
    });
  }

  @action
  onClose() {
    this.args.onClose();
    this.pageFrom = 0;
    this._selectedTab = null;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::SuggestionExplorePanel': typeof SuggestionExplorePanel;
    'reporting/automation/ai-insights/suggestion-explore-panel': typeof SuggestionExplorePanel;
  }
}
